import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Box, Toolbar } from "@mui/material";
import Navbar from "./Navbar";
import { useSidebar } from "@/context/SidebarContext";
import { useLoading } from "@/context/LoadingContext";
import { useGetMe } from "@/hooks/userHook";
import { useEffect, useState } from "react";
import NoPermission from "@/pages/NoPermission";
import { msalInstance } from "@/main";
import { usePermissions } from "@/hooks/usePermissions";
import menu from "./menu";
import { useUserInfo } from "@/context/UserInfoContext";

const ProtectedLayout = () => {
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const { isLoading } = useLoading();
  const userInfo = useUserInfo();
  const { canView } = usePermissions();
  const location = useLocation();
  const {
    data: userData,
    error: userError,
    isError: userIsError,
    isFetching: userIsFetching,
  } = useGetMe();
  const [noAuthorize, setNoAuthorize] = useState(false);
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  const isAuthenticated = !!account;

  // const drawerWidth = 240;
  const collapsedWidth = 60;

  const getResourceKeyFromPath = (pathname: string): string | null => {
    const menuItem = menu.find((item) => {
      if (item.path === "/") {
        return pathname === "/";
      }
      return pathname.startsWith(item.path);
    });
    return menuItem?.key || null;
  };

  const currentResourceKey = getResourceKeyFromPath(location.pathname);
  const isAdminPath = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (
      isAuthenticated &&
      !userIsFetching &&
      (userIsError || !userData || !userData.is_active)
    ) {
      console.error("User authorization failed:", userError);
      setNoAuthorize(true);
    } else if (!isAuthenticated) {
      setNoAuthorize(false);
    }
  }, [isAuthenticated, userData, userError, userIsError, userIsFetching]);

  if (
    noAuthorize ||
    (isAuthenticated && !isAdminPath && !canView(currentResourceKey || ""))
  ) {
    if (isLoading) {
      return null;
    }
    return <NoPermission />;
  }
  
  if (
    isAdminPath &&
    !userInfo?.groups?.some(
      (g) =>
        g.toLowerCase() === "developer" || g.toLowerCase() === "super admin",
    )
  ) {
    if (isLoading) return null;
    return <NoPermission />;
  }

  if (
    location.pathname.startsWith("/breakdown") &&
    userInfo?.groups?.some((g) => g === "buyer") &&
    !userInfo?.buyer_code
  ) {
    return <NoPermission />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        <Navbar />
        {/* Backdrop */}
        {isSidebarOpen && (
          <Box
            onClick={() => setIsSidebarOpen(false)}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1050,
              transition: "opacity 0.3s ease",
            }}
          />
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            position: "relative",
            zIndex: 1,
            marginLeft: `${collapsedWidth}px`,
            transition: "margin-left 0.3s ease",
            width: `calc(100% - ${collapsedWidth}px)`,
          }}
        >
          <Box sx={{ minHeight: "calc(100vh - 50px)" }}>
            <Toolbar />
            <Outlet />
          </Box>
          <Box
            sx={{
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              pb: 1,
              backgroundColor: "background.default",
            }}
          ></Box>
        </Box>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Navigate to="/login" replace />
      </UnauthenticatedTemplate>
    </>
  );
};

export default ProtectedLayout;
